import { useAppStore } from '~/stores'
import { Button, Popover, PopoverContent, PopoverTrigger, Switch, showDialog } from '@bpinternal/ui-kit-next'
import { Loader2 } from 'lucide-react'
import AiIcon from '~/assets/ai-icon.svg?react'
import AiIconOn from '~/assets/ai-icon-on.svg?react'
import { cn } from '~/utils'
import { Spinner } from '~/elements'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { useInsightsPlugin } from '../hooks/useInsightsPlugin'
import { useEffect, useState } from 'react'
import { AnnouncementsSearch } from '~/features/announcements/types'
import { PublishWarningDialog } from '~/features/conversations/components'
import { showPlanSelectionDialog } from '~/features/billing/components/PlanSelectionDialog'

type Props = {
  workspaceId: string
  botId: string
  isMobile: boolean
  insightsPluginState: ReturnType<typeof useInsightsPlugin>
}

export const AiButton = ({ isMobile, insightsPluginState, ...props }: Props) => {
  const { isAiEnabled, isPublishing, isBotUpdating, enableAiInsights, insightsPlugin } = insightsPluginState

  const activeWorkspace = useAppStore((state) => state.activeWorkspace)
  const needPlanUpgrade = activeWorkspace?.plan === 'community'
  const [open, setOpen] = useState(false)

  const search = useSearch({ strict: false }) as AnnouncementsSearch
  const navigate = useNavigate()

  useEffect(() => {
    if (search.aiInsights === true) {
      setOpen(true)
      navigate({
        search: undefined,
        replace: true,
      })
    }
  }, [])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          className={cn(
            'ai-button',
            'flex w-9 items-center gap-2 bg-neutral-100 text-neutral-900 hover:bg-neutral-200 dark:bg-neutral-200 dark:hover:bg-neutral-300 md:w-32'
          )}
        >
          {isBotUpdating || isPublishing ? (
            <Loader2 className="animate-spin" />
          ) : isAiEnabled ? (
            <AiIconOn />
          ) : (
            <AiIcon />
          )}
          {!isMobile && (isAiEnabled ? 'Insights On' : 'Insights Off')}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[300px] bg-neutral-50 p-3" align={isMobile ? 'end' : 'start'}>
        {needPlanUpgrade ? (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <AiIconOn className="h-4 w-4" />
              <span className="text-sm font-medium text-neutral-900">AI Insights</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Upgrade your plan to get sentiment analysis and AI summaries for incoming conversations.
            </p>
            <Button
              className="h-8 w-full text-sm font-medium"
              onClick={() => showPlanSelectionDialog({ workspaceId: props.workspaceId })}
            >
              Upgrade to Plus
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <AiIconOn className="h-4 w-4" />
              <span className="text-sm font-medium text-neutral-900">AI Insights</span>
              <span className="rounded bg-neutral-200 px-1.5 py-0.5 text-xs font-medium text-neutral-600">Beta</span>
            </div>
            <p className="text-sm text-muted-foreground">
              Quickly understand your conversations with sentiment analysis and concise AI summaries.
            </p>
            <div className="flex items-center justify-between border-t border-neutral-200 pt-3">
              <div className="flex flex-col">
                <span className="text-sm font-medium text-neutral-900">Enable</span>
                <span className="text-xs text-muted-foreground">Incurs additional cost</span>
              </div>
              {isBotUpdating || isPublishing ? (
                <Spinner size="3" color="gray" />
              ) : (
                <Switch
                  checked={isAiEnabled || false}
                  disabled={isBotUpdating || isPublishing || !insightsPlugin?.enabled}
                  onCheckedChange={(on) => {
                    showDialog(
                      ({ close }) => (
                        <PublishWarningDialog
                          botId={props.botId}
                          close={close}
                          onSubmit={() => enableAiInsights({ enabled: on, showToast: true })}
                        />
                      ),
                      {
                        title: 'Warning',
                      }
                    )
                  }}
                />
              )}
            </div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
